/* eslint-disable complexity */
import { useNavigate, useLocation } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import Button from 'carbon-react/lib/components/button';

import {
  QUERY_PARAMS,
  ROUTES,
  activateProduct,
  createDatasourceByIntegration,
  getReportingUrl,
  isInSyncing,
  PREDICT_PRODUCT_ID
} from '@futrli/shared-services';
import { UISystemIcon } from '@futrli/ui-components';
import { BillingModel, Integration } from '@futrli-sdk/app-store';
import { createOrg, createProviderConnection, getCallbackUrl } from '@futrli/organisations';
import { getOnboardingStartUrl } from '@futrli/shared-services';

import { OnboardingNavbar } from '../onboarding-navbar';
import { StoreState } from '../../../core/redux-utils/types';
import { TRACKING_EVENTS } from '../../../../../predict/src/core/constants';
import { trackEvent } from '../../../../../predict/src/core/tracker';
import { FORECASTING_TYPE, REPORTING_TYPE } from '../onboarding-start';
import { useAnimationLatency } from '../../../../../predict/src/forecast/onboarding/utils/hooks';
import { useLoadWorkspacesAndIntegrations } from './use-load-workspaces-and-integrations';
import { useSyncStatusPolling } from './use-sync-status-polling';

import * as styles from '../onboarding.scss';
import { OnboardingLoader } from '../onboarding-loader';

const STEP_INTERVAL = 2000;

export const OnboardingInitialSync = () => {
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [personalizingSetupFinished, setPersonalizingSetupFinished] = useState(false);
  const [buildingGuideFinished, setBuildingGuideFinished] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const workspaceId = searchParams.get(QUERY_PARAMS.WORKSPACE_ID);
  const organisationId = searchParams.get(QUERY_PARAMS.ORG_ID);
  const datasourceId = searchParams.get(QUERY_PARAMS.DATASOURCE_ID);
  const type = searchParams.get(QUERY_PARAMS.TYPE);
  const usedIntegration = searchParams.get(QUERY_PARAMS.INTEGRATION);

  const { organisation, integrations, isLoading: isLoadingData } = useLoadWorkspacesAndIntegrations(
    workspaceId,
    organisationId
  );
  const demoIntegrations = useMemo(() => {
    return integrations?.filter(i => i.slug === 'demo-au' || i.slug === 'demo-eu');
  }, [integrations]);
  const { error, setStartPolling, syncingFinished } = useSyncStatusPolling(datasourceId);
  const userId = useSelector((store: StoreState) => store.authReducers.authenticatedUser.username);

  const isOrgSyncing = organisation?.datasource ? isInSyncing(organisation.datasource) : undefined;
  const isUsedDemoIntegration = usedIntegration === 'demo_eu' || usedIntegration === 'demo_au';

  const showSyncingFinished = useAnimationLatency(syncingFinished && !error);
  const showPersonalizingSetup = useAnimationLatency(personalizingSetupFinished);
  const showBuildingForecastGuide = useAnimationLatency(buildingGuideFinished);

  const handleProviderClick = useCallback(
    async (integration?: Integration) => {
      if (!integration || !integrations || !workspaceId) {
        return;
      }
      setIsLoading(true);
      // setError(false);
      const isDemoOrg = integration.slug === 'demo-au' || integration.slug === 'demo-eu';

      try {
        const ds = createDatasourceByIntegration(integration.id, integrations);
        const tmpOrg = { name: 'Organisation', datasource: ds };
        const org = await createOrg(workspaceId, tmpOrg);

        await activateProduct(workspaceId, org.id, PREDICT_PRODUCT_ID, BillingModel.free);

        const response = await createProviderConnection(workspaceId, org.id, {
          workspace_id: workspaceId,
          organisation_id: org.id,
          datasource_id: org.datasource.id,
          integration:
            integration.slug === 'demo-au' ? 'demo_au' : integration.slug === 'demo-eu' ? 'demo_eu' : integration.slug,
          user_id: userId,
          provider: isDemoOrg ? 'demo' : integration.slug === 'xero' ? 'xero' : 'ledgerflow',
          success_url: getCallbackUrl(getOnboardingStartUrl())
        });

        if (response?.status === 'SUCCESS' && response?.initiation_url) {
          // markAsStale(orgSchema.key);
          switch (integration.slug) {
            case 'demo-au':
              trackEvent(TRACKING_EVENTS.ADDED_DEMO_AU_ORGANISATION);
            case 'demo-eu':
              trackEvent(TRACKING_EVENTS.ADDED_DEMO_UK_ORGANISATION);
            case 'qbo':
              trackEvent(TRACKING_EVENTS.ADDED_QBO_ORGANISATION);
            case 'sage':
              trackEvent(TRACKING_EVENTS.ADDED_SAGE_ORGANISATION);
            default:
              trackEvent(TRACKING_EVENTS.ADDED_XERO_ORGANISATION);
          }
          window.location.href = response.initiation_url;
        }
      } catch (e) {
        // setError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [integrations, userId, workspaceId]
  );

  // Start polling for sync status updates if org is syncing
  useEffect(() => {
    setStartPolling(!!isOrgSyncing);
  }, [isOrgSyncing, setStartPolling]);

  // When syncing completes, complete the syncing animation
  useEffect(() => {
    const updateAnimationSteps = async () => {
      await new Promise(resolve => setTimeout(resolve, STEP_INTERVAL));
      setPersonalizingSetupFinished(true);
      await new Promise(resolve => setTimeout(resolve, STEP_INTERVAL));
      setBuildingGuideFinished(true);
    };

    if (syncingFinished) {
      updateAnimationSteps();
    }
  }, [syncingFinished]);

  const handleLetsGoClick = useCallback(() => {
    if (!workspaceId || !organisationId) {
      return;
    }

    if (type === REPORTING_TYPE) {
      navigate(`${getReportingUrl(workspaceId, organisationId)}${ROUTES.ONBOARDING_ROUTE}`);
    } else {
      navigate(
        `${ROUTES.ONBOARDING_ROUTE}${ROUTES.ONBOARDING_INITIAL}${ROUTES.FORECAST_START}?${QUERY_PARAMS.WORKSPACE_ID}=${workspaceId}&${QUERY_PARAMS.ORG_ID}=${organisationId}`
      );
    }
  }, [navigate, organisationId, type, workspaceId]);

  // If the org isn't syncing, skip this onboarding step entirely
  // Must check for false, as isOrgSyncing can also be undefined if data isnt available yet
  useEffect(() => {
    if (isOrgSyncing === false) {
      handleLetsGoClick();
    }
  }, [handleLetsGoClick, isOrgSyncing]);

  if (!workspaceId || !organisationId || !datasourceId) {
    return null;
  }

  if (isLoading || isLoadingData || !isOrgSyncing) {
    return <OnboardingLoader />;
  }

  return (
    <>
      <OnboardingNavbar />
      <div className={styles.container} style={{ maxWidth: 668 }}>
        <div style={{ height: 200 }} />
        <h1 className={styles.title} style={{ textAlign: 'center' }}>
          <FormattedMessage id="onboarding.you_are_on_the_way" defaultMessage="You’re on your way!" />
        </h1>
        <div className={styles.border} />
        <div className={styles.progressContainer}>
          <div className={styles.description} style={{ fontSize: 24, fontWeight: 400 }}>
            <FormattedMessage id="onboarding.syncing_your_data" defaultMessage="Syncing your data" />
          </div>
          {syncingFinished && !error ? (
            <UISystemIcon
              iconName="onboarding-sync-checked"
              width={40}
              height={40}
              className={classNames(styles.fadeInContainer, {
                [styles.fadeIn]: showSyncingFinished
              })}
            />
          ) : (
            <UISystemIcon
              iconName="onboarding-sync-not-checked"
              className={classNames({
                [styles.loadingSvg]: !error
              })}
              width={40}
              height={40}
            />
          )}
        </div>
        <div className={styles.progressContainer}>
          <div className={styles.description} style={{ fontSize: 24, fontWeight: 400 }}>
            <FormattedMessage id="onboarding.personalising_your_setup" defaultMessage="Personalising your setup" />
          </div>
          {personalizingSetupFinished ? (
            <UISystemIcon
              iconName="onboarding-sync-checked"
              width={40}
              height={40}
              className={classNames(styles.fadeInContainer, {
                [styles.fadeIn]: showPersonalizingSetup
              })}
            />
          ) : (
            <UISystemIcon
              iconName="onboarding-sync-not-checked"
              className={classNames({
                [styles.loadingSvg]: !error
              })}
              width={40}
              height={40}
            />
          )}
        </div>
        <div className={styles.progressContainer}>
          <div className={styles.description} style={{ fontSize: 24, fontWeight: 400 }}>
            {type === FORECASTING_TYPE ? (
              <FormattedMessage
                id="onboarding.building_your_forecasting_guide"
                defaultMessage="Building your forecasting guide"
              />
            ) : (
              <FormattedMessage
                id="onboarding.building_your_reporting_guide"
                defaultMessage="Building your reporting guide"
              />
            )}
          </div>
          {buildingGuideFinished ? (
            <UISystemIcon
              iconName="onboarding-sync-checked"
              width={40}
              height={40}
              className={classNames(styles.fadeInContainer, {
                [styles.fadeIn]: showBuildingForecastGuide
              })}
            />
          ) : (
            <UISystemIcon
              iconName="onboarding-sync-not-checked"
              className={classNames({
                [styles.loadingSvg]: !error
              })}
              width={40}
              height={40}
            />
          )}
        </div>
        <Button
          disabled={!syncingFinished || !personalizingSetupFinished || !buildingGuideFinished || !workspaceId}
          buttonType="primary"
          size="large"
          onClick={handleLetsGoClick}
        >
          <FormattedMessage id="common.lets_go" defaultMessage="Let's go!" />
        </Button>
        {error && (
          <div className={styles.demoText} style={{ marginTop: 40, textAlign: 'left', color: '#CD384B' }}>
            <FormattedMessage
              id="common.generic_system_error"
              defaultMessage="There has been a system error, please try again or contact our support team for further assistance"
            />
          </div>
        )}
        {demoIntegrations && !isUsedDemoIntegration && !organisation?.is_externally_managed && (
          <div className={styles.demoText} style={{ marginTop: 40, textAlign: 'left' }}>
            <FormattedMessage
              id="onboarding.no_wait_email_sync_completed"
              defaultMessage="If you don’t want to wait, we’ll email you when your data sync has completed. Or use the {demoUK}, or {demoAU} if you want to start your onboarding journey now."
              values={{
                demoUK: (
                  <span
                    className={styles.demoCompanyName}
                    onClick={() => handleProviderClick(demoIntegrations.find(i => i.slug === 'demo-eu'))}
                  >
                    {intl.formatMessage({ id: 'onboarding.demo_comp_uk', defaultMessage: 'Demo Company UK' })}
                  </span>
                ),
                demoAU: (
                  <span
                    className={styles.demoCompanyName}
                    onClick={() => handleProviderClick(demoIntegrations.find(i => i.slug === 'demo-au'))}
                  >
                    {intl.formatMessage({ id: 'onboarding.demo_comp_au', defaultMessage: 'Demo Company AU' })}
                  </span>
                )
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};
